import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { formatIDR, formatDate, formatPercent } from "../lib/format";
import { ArrowLeft, GitBranch } from "lucide-react";

export default function BorrowerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/borrowers/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="p-8 text-sm text-muted-foreground">Memuat…</div>;
  const b = data.borrower;

  const totalApproved = data.facilities.reduce((s, f) => s + (f.approved_limit || 0), 0);
  const totalUtilized = data.facilities.reduce((s, f) => s + (f.utilized_limit || 0), 0);
  const totalExposure = data.exposures.reduce((s, e) => s + (e.outstanding_amount || 0), 0);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="borrower-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/borrowers")} className="mb-4" data-testid="back-button">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Kembali
      </Button>

      <PageHeader
        title={b.name}
        subtitle={`Borrower ID: ${b.borrower_id} · CIF: ${b.cif}`}
        action={
          <Button variant="outline" onClick={() => navigate(`/graph?borrower=${b.borrower_id}`)} data-testid="view-graph-button">
            <GitBranch className="h-4 w-4 mr-1.5" /> Lihat Graph
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card">
          <div className="kpi-label">Tipe</div>
          <div className="text-lg font-semibold mt-1">{b.borrower_type}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Sektor</div>
          <div className="text-lg font-semibold mt-1">{b.sector}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Risk Rating</div>
          <Badge variant="outline" className="mono text-base mt-1">{b.risk_rating}</Badge>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Status</div>
          <Badge variant={b.status === "Active" ? "default" : "secondary"}>{b.status}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="kpi-card" data-testid="bd-approved">
          <div className="kpi-label">Total Approved</div>
          <div className="kpi-value text-2xl">{formatIDR(totalApproved, { compact: true })}</div>
        </div>
        <div className="kpi-card" data-testid="bd-utilized">
          <div className="kpi-label">Total Utilized</div>
          <div className="kpi-value text-2xl">{formatIDR(totalUtilized, { compact: true })}</div>
        </div>
        <div className="kpi-card" data-testid="bd-exposure">
          <div className="kpi-label">Total Eksposur</div>
          <div className="kpi-value text-2xl">{formatIDR(totalExposure, { compact: true })}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-border rounded-md bg-card p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Group Obligor</h3>
          {data.group ? (
            <div>
              <div className="font-semibold">{data.group.group_name}</div>
              <div className="text-xs text-muted-foreground mono">{data.group.group_id}</div>
              <div className="mt-2 text-sm">Group Limit: <span className="mono">{formatIDR(data.group.group_limit, { compact: true })}</span></div>
            </div>
          ) : <div className="text-sm text-muted-foreground">Tidak ada group</div>}
        </div>
        <div className="border border-border rounded-md bg-card p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Shareholder</h3>
          {data.shareholders.length === 0 ? (
            <div className="text-sm text-muted-foreground">Tidak ada</div>
          ) : (
            <ul className="space-y-1.5">
              {data.shareholders.map((s) => (
                <li key={s.shareholder_id} className="text-sm">
                  <span className="font-medium">{s.name}</span>
                  <span className="text-xs text-muted-foreground mono ml-2">{s.shareholder_id}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-wider">Fasilitas ({data.facilities.length})</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Facility ID</th>
              <th>Tipe</th>
              <th>Produk</th>
              <th className="text-right">Approved</th>
              <th className="text-right">Utilized</th>
              <th className="text-right">Available</th>
              <th className="text-right">Util %</th>
              <th>Jatuh Tempo</th>
            </tr>
          </thead>
          <tbody>
            {data.facilities.map((f) => {
              const ratio = f.approved_limit ? f.utilized_limit / f.approved_limit : 0;
              return (
                <tr key={f.facility_id} className="cursor-pointer" onClick={() => navigate(`/facilities/${f.facility_id}`)}>
                  <td className="mono">{f.facility_id}</td>
                  <td>{f.facility_type}</td>
                  <td className="text-xs">{f.product_type}</td>
                  <td className="text-right-num">{formatIDR(f.approved_limit, { compact: true })}</td>
                  <td className="text-right-num">{formatIDR(f.utilized_limit, { compact: true })}</td>
                  <td className="text-right-num">{formatIDR(f.available_limit, { compact: true })}</td>
                  <td className={`text-right-num font-bold ${ratio > 1 ? "text-[#D0021B]" : ratio > 0.9 ? "text-[#F5A623]" : ""}`}>{formatPercent(ratio)}</td>
                  <td className="mono text-xs">{formatDate(f.maturity_date)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
